/**
 * Utility functions for optimizing React application performance
 */
import { useEffect, useRef, useState } from 'react';
import { addLazyLoadingToImages, optimizeImageElement } from './imageProcessor';

/**
 * Custom hook to optimize images in a component
 * @param {Object} ref - React ref to the component container
 */
export const useImageOptimizer = (ref) => {
  useEffect(() => {
    if (ref.current) {
      addLazyLoadingToImages(ref.current);
      
      // Optimize all images in the container
      const images = ref.current.querySelectorAll('img');
      images.forEach(img => {
        optimizeImageElement(img);
      });
    }
  }, [ref]);
};

/**
 * Custom hook for lazy loading components
 * @param {number} threshold - Intersection threshold (0-1)
 * @returns {[React.RefObject, boolean]} - [ref, isVisible]
 */
export const useLazyLoad = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

/**
 * Custom hook to defer non-critical operations
 * @param {Function} callback - Function to execute
 * @param {number} delay - Delay in ms (default: 1000)
 */
export const useDeferredOperation = (callback, delay = 1000) => {
  useEffect(() => {
    // Use requestIdleCallback if available, otherwise setTimeout
    const timeoutId = window.requestIdleCallback 
      ? window.requestIdleCallback(() => callback(), { timeout: delay })
      : setTimeout(callback, delay);
    
    return () => {
      if (window.requestIdleCallback) {
        window.cancelIdleCallback(timeoutId);
      } else {
        clearTimeout(timeoutId);
      }
    };
  }, [callback, delay]);
};

/**
 * Optimize font loading
 */
export const optimizeFontLoading = () => {
  // Add font-display: swap to all font faces
  const style = document.createElement('style');
  style.textContent = `
    @font-face {
      font-display: swap !important;
    }
  `;
  document.head.appendChild(style);
};

/**
 * Preload critical resources
 * @param {Array<string>} resources - Array of URLs to preload
 */
export const preloadResources = (resources) => {
  resources.forEach(resource => {
    const link = document.createElement('link');
    link.rel = 'preload';
    
    if (resource.endsWith('.js')) {
      link.as = 'script';
    } else if (resource.endsWith('.css')) {
      link.as = 'style';
    } else if (resource.endsWith('.jpg') || resource.endsWith('.jpeg') || 
               resource.endsWith('.png') || resource.endsWith('.webp') || 
               resource.endsWith('.gif')) {
      link.as = 'image';
    } else if (resource.endsWith('.woff2') || resource.endsWith('.woff')) {
      link.as = 'font';
      link.crossOrigin = 'anonymous';
    }
    
    link.href = resource;
    document.head.appendChild(link);
  });
};

/**
 * Debounce function to limit the rate at which a function can fire
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in ms
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function to limit the rate at which a function can fire
 * @param {Function} func - Function to throttle
 * @param {number} limit - Limit in ms
 * @returns {Function} - Throttled function
 */
export const throttle = (func, limit = 300) => {
  let inThrottle;
  
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
};
